<script setup lang="ts">
import { datadogRum } from '@datadog/browser-rum'
import { useDatalayerSetup } from '@integration-layer/composables/useGAEvent/useDatalayerSetup/useDatalayerSetup'
import { useParamPage } from '@integration-layer/composables/useGAEvent/useDatalayerSetup/useParamPage'

const app = useNuxtApp()

const { isIos } = useDevice()

const { deferredBrandTheme } = await useTheme()

const { getLocale } = useI18n()
const locale = getLocale()
const route = useRoute()
const { initCart, initCompare } = useCart()
const { checkoutUrl, cartUrl } = useRouteHelper()
const { mergeWishlists, refreshWishlistOnTabChange, initWishlist } =
  useWishlist()
const { addWishlistTabChangeListener, removeWishlistTabChangeListener } =
  refreshWishlistOnTabChange()

const isCheckoutPage = computed(() => route.name === 'localized-checkout')
const isCartPage = computed(() => route.path.includes(cartUrl))
const { checkScript } = useXgen()

let isDatalayerSetupInitialized = false

onMounted(async () => {
  await initCart()
  await initWishlist()
  // randomUUID if present, random 10 digits number if possible
  useRiskified(self?.crypto?.randomUUID?.() ?? Math.floor(Math.random() * 1e10))
  useSizebay()
  useDatacloud()
  checkScript()
  addWishlistTabChangeListener()
  await mergeWishlists()
  if (isCheckoutPage.value || isCartPage.value) {
    initCompare()
  }
  useDatalayerSetup()
  const { customer } = await useCustomer()
  if (customer?.value?.id) {
    datadogRum.setUser({
      id: customer.value.id.replace('auth0|', ''),
      name: customer.value.firstName + ' ' + customer.value.lastName,
      email: customer.value.email,
    })
  } else {
    datadogRum.setUser({})
  }
  //register to service worker
  if ('serviceWorker' in navigator) {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'UPDATE_SERVICE_WORKER',
      })
    } else {
      console.log('No active service worker found.')
    }
  }
})

onBeforeUnmount(() => {
  removeWishlistTabChangeListener()
})

useHead({
  htmlAttrs: {
    lang: locale.split('-')[0],
  },
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1',
    },
  ],
})

// Trigger send event on route change
useScriptEventPage(async () => {
  // Page is a generic page only if is not amplience or product-based
  if (!(app.payload['is_product'] || !!app.payload['amplience_page_type'])) {
    useParamPage('genericPage')
  }
  if (isDatalayerSetupInitialized) {
    useDatalayerSetup()
  }
  isDatalayerSetupInitialized = true
})
// Use SSR-safe IDs for Headless UI
// https://github.com/P4sca1/nuxt-headlessui#quick-setup
provideHeadlessUseId(() => useId())
const isDatadogTest = useState<boolean>('isDatadogTest')
</script>

<template>
  <div>
    <UtilsSkipLink />
    <NuxtLoadingIndicator
      class="bg-background-primary"
      :class="`theme-${deferredBrandTheme}`"
      :color="false"
      :height="2"
    />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <AtomsLoaderArmani />
    <ClientOnly v-if="!isDatadogTest">
      <CookieBanner />
    </ClientOnly>
  </div>
</template>

<style lang="scss">
#onetrust-consent-sdk {
  display: none;
}
</style>

<style lang="scss">
@import '@design-system/assets/css/tailwind.scss';
</style>
